import { MarketplaceStoreEditionType } from '@atlassian/mpac-types/graphql-gateway-types';
const EDITION_TYPES = [MarketplaceStoreEditionType.Advanced, MarketplaceStoreEditionType.Standard];

/**
 * Returns the applicable offering based on the edition type.
 * If an edition type is provided, it attempts to find an offering that includes the edition type in its name.
 * If no edition type is provided, it returns the first offering from the list.
 */
export const getApplicableOfferingForEdition = (eligibleOfferings, editionType) => {
  if (!eligibleOfferings) return null;
  if (editionType) {
    return eligibleOfferings.find(offering => offering && offering.name.toLowerCase().includes(editionType.toLowerCase()));
  }
  return null;
};

/**
 * Checks if eligible offerings allow the user to install the app for free on a sandbox site for the edition type.
 * If no edition type is provided, it defaults to false
 */
export function checkSandboxEligibilityFromOfferingsByEdition(eligibleOfferings, editionType) {
  if (!eligibleOfferings) return false;
  const applicableOffering = getApplicableOfferingForEdition(eligibleOfferings, editionType);
  return Boolean(applicableOffering === null || applicableOffering === void 0 ? void 0 : applicableOffering.isSandbox);
}

/**
 * Determines sandbox eligibility from the eligible offerings.
 * Returns an object indicating if any offering is sandbox and the edition type for the offering if applicable.
 */
export function checkSandboxEligibilityFromOfferings(eligibleOfferings) {
  if (!eligibleOfferings) return {
    isSandbox: false,
    edition: null
  };
  const applicableOffering = eligibleOfferings.find(offering => offering && offering.isSandbox);
  return {
    isSandbox: Boolean(applicableOffering === null || applicableOffering === void 0 ? void 0 : applicableOffering.isSandbox),
    edition: EDITION_TYPES.find(edition => applicableOffering === null || applicableOffering === void 0 ? void 0 : applicableOffering.name.toLowerCase().includes(edition.toLowerCase()))
  };
}

/**
 * Checks if there is an applicable offering with MIL eligibility based on the edition type.
 * It checks the specific edition type if provided, otherwise it defaults to false.
 */
export function checkMILEligibilityFromOfferingsByEdition(eligibleOfferings, editionType) {
  if (!eligibleOfferings) return false;
  const applicableOffering = getApplicableOfferingForEdition(eligibleOfferings, editionType);
  return Boolean(applicableOffering === null || applicableOffering === void 0 ? void 0 : applicableOffering.isInstance);
}

/**
 * Determines MIL eligibility from the eligible offerings.
 * Returns an object indicating if any offering is a MIL instance and the edition type for the offering if applicable.
 */
export function checkMILEligibilityFromOfferings(eligibleOfferings) {
  if (!eligibleOfferings) return {
    isInstance: false,
    edition: null
  };
  const applicableOffering = eligibleOfferings.find(offering => offering && offering.isInstance);
  return {
    isInstance: Boolean(applicableOffering === null || applicableOffering === void 0 ? void 0 : applicableOffering.isInstance),
    edition: EDITION_TYPES.find(edition => applicableOffering === null || applicableOffering === void 0 ? void 0 : applicableOffering.name.toLowerCase().includes(edition.toLowerCase()))
  };
}

/**
 * Determines the parent capability set based on the eligible offerings.
 * It checks combinations of offerings to identify if they match a specific capability set.
 * More details: https://hello.atlassian.net/wiki/spaces/~70121d4fc612ab88f45ef9920bf96bf94faef/pages/4888627640/MIL+Editions+Sandbox+eligible+Offerings+Scenarios
 */
export function getParentCapabilitySetFromOfferings(eligibleOfferings) {
  if (!eligibleOfferings) return false;
  const offeringsSet = new Set(eligibleOfferings.map(offering => offering === null || offering === void 0 ? void 0 : offering.name.toUpperCase()));
  const offeringCombinations = [{
    offerings: ['STANDARD_INSTANCE SANDBOX', 'ADVANCED'],
    capabilitySet: 'capabilityStandard'
  }, {
    offerings: ['STANDARD SANDBOX', 'ADVANCED'],
    capabilitySet: 'capabilityStandard'
  }, {
    offerings: ['STANDARD_INSTANCE SANDBOX', 'ADVANCED_INSTANCE SANDBOX'],
    capabilitySet: 'capabilityAdvanced'
  }, {
    offerings: ['STANDARD SANDBOX', 'ADVANCED SANDBOX'],
    capabilitySet: 'capabilityAdvanced'
  }];
  for (const combination of offeringCombinations) {
    if (combination.offerings.every(offering => offeringsSet.has(offering))) {
      return combination.capabilitySet;
    }
  }
  return null;
}

/**
 * Maps the capability set determined from the offerings to a parent edition.
 * It returns the corresponding edition if a valid capability set is found.
 */
export function getParentEditionFromOfferings(eligibleOfferings) {
  const capabilityToEditionMap = {
    capabilityStandard: MarketplaceStoreEditionType.Standard,
    capabilityAdvanced: MarketplaceStoreEditionType.Advanced
  };
  const parentCapabilitySet = getParentCapabilitySetFromOfferings(eligibleOfferings);
  return parentCapabilitySet ? capabilityToEditionMap[parentCapabilitySet] : null;
}

/**
 * Returns the id of the applicable offering based on the eligible offerings.
 */
export const getOfferingIdFromEligibleOfferings = _ref => {
  let {
    eligibleOfferings,
    edition,
    selfServeAppEditionsEnabled,
    isEditionsSupported,
    isMILEnabled
  } = _ref;
  if ((selfServeAppEditionsEnabled || isMILEnabled) && eligibleOfferings !== null && eligibleOfferings !== void 0 && eligibleOfferings.length) {
    const applicableOffering = getApplicableOfferingForEdition(eligibleOfferings,
    // If app does not support editions and edition feature flag is enabled then
    // Standard should be the default edition for backward compatibility
    isEditionsSupported ? edition : MarketplaceStoreEditionType.Standard);
    return applicableOffering ? applicableOffering.id : null;
  }
  return null;
};

/**
 * Checks if the edition is charged separately in sandbox based on the eligible offerings and edition type.
 * Return true if the parent edition is standard and the given edition type is advanced.
 */

export const checkIfEditionChargedSeparatelyInSandboxFromOfferingsByEdition = (eligibleOfferings, editionType) => {
  return Boolean(checkSandboxEligibilityFromOfferings(eligibleOfferings).isSandbox && getParentEditionFromOfferings(eligibleOfferings) === MarketplaceStoreEditionType.Standard && editionType === MarketplaceStoreEditionType.Advanced);
};